import React, { ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	checkAllProducts,
	clearAllProducts,
	clearAllTags,
	toggleTagFilter,
} from '../../../store/actions/admin/products';
import { attributeMap } from './products-main';
import classNames from 'classnames';
import { HeaderArrows } from '../content/list-headers';
import { SORT_METHODS } from '../../../store/utils/content-utils';
import { ICONS } from '../../general-ui/icon';
import { EventsState } from '../../../store/types';
import Paging from '../../general-ui/paging/paging';
import CustomCheckbox from '../../general-ui/checkbox/custom-checkbox';
import './all-products.scss';

interface AllProductProps {
	productsList: JSX.Element[];
	attributes: attributeMap[];
	sortState: {
		sortBy: string;
		setSortBy: (sort: string) => void;
	};
	pagingState: {
		page: number;
		totalPages: number;
		setPage: (page: number) => void;
	};
	idList: number[];
}

export default function AllProducts({
	productsList,
	attributes,
	idList,
	sortState: { sortBy, setSortBy },
	pagingState,
}: AllProductProps): JSX.Element {
	const {
		ProductsReducer: { checkedSet, tagSet },
		ContentReducer: { view },
	} = useSelector((state: EventsState) => state);
	const dispatch = useDispatch();
	const selectAll = (isChecked: boolean) => {
		//isChecked is the value the user has just switched to
		if (isChecked) {
			dispatch(checkAllProducts(idList));
		} else {
			dispatch(clearAllProducts());
		}
	};
	const clearAll = () => dispatch(clearAllTags());
	const handleNameSort = () => {
		if (sortBy === SORT_METHODS.AZ) { setSortBy(SORT_METHODS.ZA); }
		else { setSortBy(SORT_METHODS.AZ); }
	};
	const handleUploadedSort = () => {
		if (sortBy === SORT_METHODS.NEWEST) { setSortBy(SORT_METHODS.OLDEST); }
		else { setSortBy(SORT_METHODS.NEWEST); }
	};
	const allChecked = idList.length === checkedSet.size;
	const isName = sortBy === SORT_METHODS.AZ || sortBy === SORT_METHODS.ZA;
	const isUploaded = sortBy === SORT_METHODS.NEWEST || sortBy === SORT_METHODS.OLDEST;
	return (
		<div className="all-products">
			<ul className="attributes-list">
				<li className="clear-all">
					<button
						className={classNames('no-style', {
							active: tagSet.size,
						})}
						onClick={clearAll}
					>
						Clear All
					</button>
				</li>
				{attributes.map(({ title, values }) => {
					return (
						<Attributes
							key={title}
							title={title}
							values={Array.from(values)}
							tagSet={tagSet}
						/>
					);
				})}
			</ul>
			<div>
				<ul className={classNames('products-list', view)}>
					{view === 'list' && (
						<li className="product-item list-item list-headers">
							<CustomCheckbox
								size={16}
								checked={allChecked}
								onChange={selectAll}
								name="List-Check-All"
								selectedIconName={ICONS.SQUARE_CHECKBOX_ALL}
								unselectedIconName={
									ICONS.SQUARE_CHECKBOX_UNSELECTED
								}
							/>
							<div className="grid-span" onClick={handleNameSort}>
								<span
									className={classNames('header', {
										on: isName,
									})}
								>
									NAME
								</span>
								<HeaderArrows
									up={sortBy === SORT_METHODS.AZ}
									down={sortBy === SORT_METHODS.ZA}
								/>
							</div>
							<br />
							<div onClick={handleUploadedSort}>
								<span
									className={classNames('header', {
										on: isUploaded,
									})}
								>
									UPLOADED
								</span>
								<HeaderArrows
									up={sortBy === SORT_METHODS.NEWEST}
									down={sortBy === SORT_METHODS.OLDEST}
								/>
							</div>
						</li>
					)}
					{productsList}
					<li className="paging-li">
						<Paging {...pagingState} />
					</li>
				</ul>
			</div>
		</div>
	);
}

interface AttributesProps {
	title: string;
	values: string[];
	tagSet: Set<string>;
}
function Attributes({ title, values, tagSet }: AttributesProps) {
	const dispatch = useDispatch();
	const handleToggle = ({ target }: ChangeEvent<HTMLInputElement>) =>
		dispatch(toggleTagFilter(target.value));
	return (
		<li>
			<p className="attribute-title">{title}</p>
			<div className="tag-list">
				{values.map((value) => (
					<React.Fragment key={value}>
						<input
							onChange={handleToggle}
							className="attribute-tag"
							checked={tagSet.has(value)}
							type="checkbox"
							value={value}
							id={`${value}-tag-checkbox`}
						/>
						<label htmlFor={`${value}-tag-checkbox`}>{value}</label>
					</React.Fragment>
				))}
			</div>
		</li>
	);
}
